import { parseMoney } from '@betterment/core';
import { ErrorBoundary, createConfig, createFlowContext } from '@betterment/shared-components';
import { Outlet, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { IdempotencyProviderWithFeatureRegistry } from '../../../RetailApp/graphql/idempotency/IdempotencyProviderWithFeatureRegistry';
import { nameAndImage } from '../sharedSchemas';

export const monthlyExpenses = Yup.object({
  monthlyExpensesAmount: Yup.string().required(),
});

export const COVERAGE_OPTIONS = ['3', '6', '9', '12', 'custom'] as const;

export const coverage = Yup.object({
  selectedCoverage: Yup.string().oneOf(COVERAGE_OPTIONS).optional(),
});

const coverageWithOption = (option: (typeof COVERAGE_OPTIONS)[number]) =>
  Yup.object({
    selectedCoverage: Yup.mixed().oneOf([option]).required(),
  });

export const customCoverage = Yup.object({
  targetAmount: Yup.string()
    .required('Target amount is required')
    .test('number over 1', 'Amount must be at least $1', ta => {
      const money = parseMoney(ta);
      return money ? money.cents >= 1 : false;
    }),
});

export const EMERGENCY_FUND_FLOW_CONFIG = createConfig({
  nameAndImage: {
    schema: nameAndImage,
    dependencies: Yup.object({}),
  },
  monthlyExpenses: {
    schema: monthlyExpenses,
    dependencies: Yup.object({
      nameAndImage,
    }),
  },
  coverage: {
    schema: coverage,
    dependencies: Yup.object({
      monthlyExpenses,
    }),
  },
  customCoverage: {
    schema: customCoverage,
    dependencies: Yup.object({
      coverage: coverageWithOption('custom'),
      monthlyExpenses,
    }),
  },
});

export const { FlowProvider, useStep: useEmergencyFundStep } = createFlowContext(EMERGENCY_FUND_FLOW_CONFIG);

export const EmergencyFundFlow = () => {
  const { flowId } = useParams<{ flowId: string }>();

  return (
    <ErrorBoundary fallbackType="full">
      <FlowProvider flowId={flowId}>
        <IdempotencyProviderWithFeatureRegistry featureName="completeDraftPurposeFlow">
          <Outlet />
        </IdempotencyProviderWithFeatureRegistry>
      </FlowProvider>
    </ErrorBoundary>
  );
};
